import { computed, DestroyRef, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DynamicForm, DynamicFormElement } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import { ComparisonVersionSelectorModalComponent } from 'listings/components/comparison-version-selector/comparison-version-selector-modal/comparison-version-selector-modal.component';
import { Comparison } from 'listings/components/comparison-version-selector/comparison-version-selector.component';
import { ListingChannelComparisonService } from 'listings/services/listing-channel-comparison.service';
import { ListingComparisonService } from 'listings/services/listing-comparison.service';
import { ListingsService } from 'listings/services/listings.service';
import { RoomOverviewGeneralService } from 'listings/services/rooms/room-overview-general.service';
import { RoomsFeatureGroupsService } from 'listings/services/rooms/rooms-feature-groups.service';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ComparisonType } from 'settings/enums/comparison.enum';
import { ContextAwareSidebarItem } from 'shared/interfaces/context-aware-sidebar.interface';
import { FeatureGroup } from 'shared/interfaces/feature-group.interface';
import { EventBusService, ModalService } from 'shared/services';
import { ContextAwareSidebarService } from 'shared/services/context-aware-sidebar.service';

export type ComparisonMode = 'version' | 'channel' | null;

const ROOM_TYPES_FIELD_NAME = 'roomTypes';

export interface ChangedFeatureGroupDescriptor {
    id?: string | null;
    label?: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class ListingComparisonOrchestratorService {
    private readonly contextAwareSidebarService = inject(ContextAwareSidebarService);
    private readonly roomOverviewGeneralService = inject(RoomOverviewGeneralService);
    private readonly roomsFeatureGroupsService = inject(RoomsFeatureGroupsService);
    private readonly listingChannelComparisonService = inject(ListingChannelComparisonService);
    private readonly listingsService = inject(ListingsService);
    private readonly listingComparisonService = inject(ListingComparisonService);
    private readonly modalService = inject(ModalService);
    private readonly eventBusService = inject(EventBusService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly currentVersionSignal = signal<number>(-1);
    private readonly maxVersionSignal = signal<number>(0);
    private readonly currentListingVersionChangedFeatureGroups = signal<ChangedFeatureGroupDescriptor[]>([]);

    public readonly currentVersion: Signal<number> = this.currentVersionSignal.asReadonly();
    public readonly maxVersion: Signal<number> = this.maxVersionSignal.asReadonly();

    public readonly isVersionComparisonActive = computed(() => this.currentVersionSignal() >= 0);
    public readonly isChannelComparisonActive = computed(() => !!this.listingChannelComparisonService.activeChannelSnapshot());

    public readonly comparisonMode = computed<ComparisonMode>(() => {
        if (this.isChannelComparisonActive()) {
            return 'channel';
        }
        if (this.isVersionComparisonActive()) {
            return 'version';
        }
        return null;
    });

    public readonly isComparisonActive = computed(() => this.comparisonMode() !== null);

    public readonly highlightedFeatureGroupLabels = computed<Set<string>>(() => {
        const mode = this.comparisonMode();
        if (mode === 'version') {
            const versionChangedFeatureGroups = this.currentListingVersionChangedFeatureGroups();
            return new Set(
                versionChangedFeatureGroups.map((featureGroup) => featureGroup.label).filter((label): label is string => !!label),
            );
        }
        if (mode === 'channel') {
            const channelModifiedFeatureGroups = this.listingChannelComparisonService.comparisonSourceModifiedFeatureGroups();
            return new Set(channelModifiedFeatureGroups?.map((featureGroup) => featureGroup.label) ?? []);
        }
        return new Set<string>();
    });

    private readonly roomNavigationFeatureDetailIds = toSignal(
        combineLatest([
            toObservable(this.contextAwareSidebarService.sidebarData),
            toObservable(this.roomOverviewGeneralService.selectedRoomId),
            toObservable(this.isChannelComparisonActive),
        ]).pipe(
            debounceTime(0),
            switchMap(([sidebarItems, roomId, isChannelComparisonActive]) => {
                const navigationItems = (sidebarItems ?? [])
                    .flatMap((item) => [item, ...(item.children ?? [])])
                    .filter((item) => !!item.fieldName);

                if (!roomId || !isChannelComparisonActive || navigationItems.length === 0) {
                    return of(new Map<string, string[]>());
                }

                return forkJoin(
                    navigationItems.map((item) =>
                        this.getNavigationItemFeatureDetailIds(item).pipe(
                            map((featureDetailIds) => [item.fieldName!, featureDetailIds] as const),
                        ),
                    ),
                ).pipe(map((entries) => new Map<string, string[]>(entries)));
            }),
        ),
        { initialValue: new Map<string, string[]>() },
    );

    public readonly highlightedRoomNavigationFieldNames = computed<Set<string>>(() => {
        const roomIndex = this.roomOverviewGeneralService.selectedRoomIndex();
        const structure = this.roomOverviewGeneralService.roomTypeFormStructure();
        const featureDetailIdsByFieldName = this.roomNavigationFeatureDetailIds();

        if (this.comparisonMode() !== 'channel' || roomIndex === null || !structure) {
            return new Set<string>();
        }

        const roomPrefix = `roomType.roomTypes[${roomIndex}].`;
        const roomDifferencePaths = (this.listingChannelComparisonService.comparisonSourceModifiedFeatureGroups() ?? [])
            .flatMap((featureGroup) => Object.values(featureGroup.differences).flatMap((entries) => entries ?? []))
            .flatMap((entry) => Object.keys(entry))
            .map((key) => this.listingChannelComparisonService.normalizePathForComparison(key))
            .filter((path) => path.startsWith(roomPrefix))
            .map((path) => path.slice(roomPrefix.length));

        const roomFieldPaths = this.getRoomFieldPaths(structure);
        const highlightedFieldNames = new Set<string>();

        featureDetailIdsByFieldName.forEach((featureDetailIds, fieldName) => {
            const fieldPaths = featureDetailIds
                .map((featureDetailId) => roomFieldPaths.get(featureDetailId))
                .filter((fieldPath): fieldPath is string => !!fieldPath)
                .map((fieldPath) => this.listingChannelComparisonService.normalizePathForComparison(fieldPath));

            const hasDifference = roomDifferencePaths.some((differencePath) =>
                fieldPaths.some(
                    (fieldPath) =>
                        differencePath === fieldPath ||
                        differencePath.startsWith(`${fieldPath}.`) ||
                        differencePath.startsWith(`${fieldPath}[`),
                ),
            );

            if (hasDifference) {
                highlightedFieldNames.add(fieldName);
            }
        });

        return highlightedFieldNames;
    });

    public readonly bannerInfo = computed(() => {
        const mode = this.comparisonMode();
        if (mode === 'version') {
            return {
                mode,
                versionNumber: this.currentVersionSignal(),
                maxVersion: this.maxVersionSignal(),
                channelName: null as string | null,
            };
        }
        if (mode === 'channel') {
            const activeSnapshot = this.listingChannelComparisonService.activeChannelSnapshot();
            return {
                mode,
                versionNumber: null as number | null,
                maxVersion: null as number | null,
                channelName: activeSnapshot?.channelName ?? null,
            };
        }
        return null;
    });

    public readonly canOpenVersionComparison = computed(() => {
        const listing = this.contextAwareSidebarService.listing();
        if (!listing) {
            return false;
        }
        const maxVersion = listing.maxVersion ?? 0;
        return maxVersion > 1 && !listing.isReadOnly && !this.isComparisonActive();
    });

    constructor() {
        this.subscribeToListingId();
        this.subscribeToListingCompareEvents();
        this.syncMaxVersionFromListing();
    }

    public openVersionComparisonModal(): void {
        const listingId = this.contextAwareSidebarService.listingId();
        const outletId = this.contextAwareSidebarService.outletId();
        const featureGroupName = this.listingChannelComparisonService.tabFieldName();

        if (!listingId || !featureGroupName) {
            return;
        }

        this.listingsService
            .getListingVersions(listingId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((versions: any[]) => {
                    const orderedVersions = versions.orderBy('version', false).map((versionItem: any) => {
                        versionItem.label = this.translateService.instant('listings.header.version', {
                            versionNumber: versionItem.version,
                            date: versionItem.label.split('||')[1],
                        });
                        return versionItem;
                    });

                    return this.modalService.openModal(ComparisonVersionSelectorModalComponent, {
                        type: 'top',
                        maxVersion: this.maxVersionSignal(),
                        versions: orderedVersions,
                        outletId: outletId ?? undefined,
                        listingId,
                        featureGroupName,
                    }).onClose;
                }),
            )
            .subscribe((selectedVersion: number) => {
                if (selectedVersion) {
                    this.startVersionComparison(selectedVersion);
                }
            });
    }

    public endComparison(): void {
        const mode = this.comparisonMode();
        if (mode === 'version') {
            this.endVersionComparison();
        } else if (mode === 'channel') {
            this.endChannelComparison();
        }
    }

    private startVersionComparison(version: number): void {
        const listingId = this.contextAwareSidebarService.listingId();
        const outletId = this.contextAwareSidebarService.outletId();
        const featureGroupName = this.listingChannelComparisonService.tabFieldName();

        if (!listingId || !featureGroupName || Number.isNaN(version)) {
            return;
        }

        this.currentVersionSignal.set(version);

        const changedFeatureGroupsObservable = outletId
            ? this.listingsService.getChangedFeatureGroups(version, undefined, outletId)
            : this.listingsService.getChangedFeatureGroups(version, listingId, undefined);

        this.listingsService
            .getListingByIdAndVersion(listingId, version)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((listingResponse) => combineLatest([of(listingResponse), changedFeatureGroupsObservable])),
            )
            .subscribe(([comparisonData, changedFeatureGroupsData]) => {
                const changedFeatureGroups = (changedFeatureGroupsData as unknown as ChangedFeatureGroupDescriptor[]) ?? [];
                this.currentListingVersionChangedFeatureGroups.set(changedFeatureGroups);

                this.refreshFeatureGroupSearch();

                const comparisonPayload: Comparison = this.listingComparisonService.prepareComparisonObject(
                    comparisonData,
                    outletId ?? undefined,
                    featureGroupName,
                    changedFeatureGroups as unknown as FeatureGroup[],
                    version,
                    undefined,
                    ComparisonType.VERSION,
                );

                this.eventBusService.dispatch('listing:compare', comparisonPayload);
                this.eventBusService.dispatch('listing:version', {
                    listingId,
                    version,
                    changedFeatureGroupsList: changedFeatureGroups,
                });
            });
    }

    private endVersionComparison(): void {
        const listingId = this.contextAwareSidebarService.listingId();

        this.currentVersionSignal.set(-1);
        this.currentListingVersionChangedFeatureGroups.set([]);

        this.eventBusService.dispatch('listing:compare', undefined);
        this.refreshFeatureGroupSearch();

        if (listingId) {
            this.eventBusService.dispatch('listing:version', {
                listingId,
                version: -1,
                changedFeatureGroups: null,
            });
        }
    }

    private endChannelComparison(): void {
        this.listingChannelComparisonService.resetService();
    }

    private refreshFeatureGroupSearch(): void {
        const currentSearchTerm = this.eventBusService.getEventStreamCurrentValue('featureGroupSearch');
        this.eventBusService.dispatch('featureGroupSearch', undefined);
        if (currentSearchTerm !== undefined) {
            this.eventBusService.dispatch('featureGroupSearch', currentSearchTerm);
        }
    }

    private subscribeToListingId(): void {
        toObservable(this.contextAwareSidebarService.listingId)
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((newListingId) => {
                if (!newListingId && this.isVersionComparisonActive()) {
                    this.endVersionComparison();
                }
            });
    }

    private subscribeToListingCompareEvents(): void {
        this.eventBusService
            .getObservableForEventStream('listing:compare', true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((eventData: any) => {
                if (eventData?.changedFeatureGroups) {
                    this.currentListingVersionChangedFeatureGroups.set(eventData.changedFeatureGroups);
                }
                if (eventData?.currentVersion !== undefined && eventData.currentVersion >= 0) {
                    this.currentVersionSignal.set(eventData.currentVersion);
                }
            });
    }

    // Feature details of a room navigation item: amenity children carry it inline, the other items need a request
    private getNavigationItemFeatureDetailIds(item: ContextAwareSidebarItem): Observable<string[]> {
        if (item.featureDetailId) {
            return of([item.featureDetailId]);
        }

        return this.roomsFeatureGroupsService.getNavigationFeatureDetails(item.id).pipe(
            map((response) => response?.featureDetailIds ?? []),
            catchError(() => of([])),
        );
    }

    // Maps each room field id to its path inside a room of the roomType form structure
    private getRoomFieldPaths(structure: DynamicForm): Map<string, string> {
        const roomTypesCard = structure.inputs.find((input) => input.name === ROOM_TYPES_FIELD_NAME);
        const roomFields = roomTypesCard?.subInputs?.find((subInput) => subInput.name === ROOM_TYPES_FIELD_NAME)?.subInputs ?? [];
        const fieldPaths = new Map<string, string>();

        const collectFieldPaths = (fields: DynamicFormElement[], parentPath: string) => {
            fields.forEach((field) => {
                const fieldPath = parentPath ? `${parentPath}.${field.name}` : field.name;
                if (field.fieldId) {
                    fieldPaths.set(field.fieldId, fieldPath);
                }
                collectFieldPaths(field.subInputs ?? [], fieldPath);
            });
        };

        collectFieldPaths(roomFields, '');
        return fieldPaths;
    }

    private syncMaxVersionFromListing(): void {
        effect(() => {
            const listing = this.contextAwareSidebarService.listing();
            if (listing?.maxVersion !== undefined && listing.maxVersion !== null) {
                this.maxVersionSignal.set(listing.maxVersion);
            }
        });
    }
}
